import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, Menu, MenuItem, SvgIcon, Tooltip,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import UndoIcon from '@material-ui/icons/Undo';
import { ProductImage } from './MerchandisingWorkbenchUi';

export const PushPinIcon = () => (
  <SvgIcon>
    <path d="M16 9V4h1V2H7v2h1v5c0 1.1-.9 2-2 2v2h5.2v7h1.6v-7H18v-2c-1.1 0-2-.9-2-2z" />
  </SvgIcon>
);

export const ProductCard = ({
  product, change, displayPosition, dragging, dropTarget, groupExcluded, groupFactor, copy,
  onHide, onPin, onDragStart, onDragEnter, onDragEnd, onDropAt, onMoveEarlier,
  onMoveLater, onUnpin, interactive,
}) => {
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const pinned = change?.TYPE === 'pinned' || change?.TYPE === 'added';
  const hidden = change?.TYPE === 'hidden';
  const blocked = hidden || groupExcluded;
  const groupAdjusted = !blocked && !pinned && groupFactor !== 1;
  return (
    <article
      className={`mp-product-card ${pinned ? 'is-pinned' : ''} ${hidden ? 'is-hidden' : ''} ${groupExcluded ? 'is-group-excluded' : ''} ${dragging ? 'is-dragging' : ''} ${dropTarget ? 'is-drop-target' : ''}`}
      draggable={interactive && !blocked}
      onDragStart={(event) => interactive && !blocked && onDragStart(event, product.ID)}
      onDragOver={(event) => { if (interactive && !blocked) event.preventDefault(); }}
      onDragEnter={() => interactive && !blocked && onDragEnter(product.ID)}
      onDragEnd={onDragEnd}
      onDrop={(event) => interactive && !blocked && onDropAt(event, product.ID)}
    >
      <div className="mp-product-card-media">
        <ProductImage product={product} />
        {interactive && !blocked && (
          <>
            <Tooltip title={copy.REORDER_PRODUCT}>
              <IconButton
                className="mp-drag-signifier"
                aria-label={`${copy.REORDER_PRODUCT} ${product.PRODUCT_NAME}`}
                aria-haspopup="menu"
                aria-expanded={Boolean(moveMenuAnchor)}
                onClick={(event) => setMoveMenuAnchor(event.currentTarget)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    onMoveEarlier();
                  } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                    event.preventDefault();
                    onMoveLater();
                  }
                }}
              >
                <DragIndicatorIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={moveMenuAnchor}
              open={Boolean(moveMenuAnchor)}
              onClose={() => setMoveMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <MenuItem onClick={() => { onMoveEarlier(); setMoveMenuAnchor(null); }}>
                {copy.MOVE_EARLIER}
              </MenuItem>
              <MenuItem onClick={() => { onMoveLater(); setMoveMenuAnchor(null); }}>
                {copy.MOVE_LATER}
              </MenuItem>
            </Menu>
          </>
        )}
        {!blocked && pinned && <span className="mp-product-position">{displayPosition}</span>}
        {interactive && !groupExcluded && (
          <div className="mp-card-primary-actions">
            <Tooltip title={hidden ? copy.RESTORE : copy.HIDE}>
              <IconButton className="mp-card-hide" aria-label={`${hidden ? copy.RESTORE : copy.HIDE} ${product.PRODUCT_NAME}`} onClick={onHide}>
                {hidden ? <UndoIcon /> : <CloseIcon />}
              </IconButton>
            </Tooltip>
            {!hidden && (
              <Tooltip title={pinned ? copy.UNPIN : copy.PIN_AFTER_CURRENT}>
                <IconButton className="mp-card-pin" aria-label={`${pinned ? copy.UNPIN : copy.PIN_AFTER_CURRENT} ${product.PRODUCT_NAME}`} aria-pressed={pinned} onClick={pinned ? onUnpin : onPin}>
                  <PushPinIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
        {hidden && <span className="mp-product-overlay">{copy.HIDDEN}</span>}
        {!hidden && groupExcluded && <span className="mp-product-overlay mp-product-overlay--group">{copy.EXCLUDED_BY_GROUP}</span>}
        {groupAdjusted && (
          <span className={`mp-product-group-effect ${groupFactor > 1 ? 'is-increased' : 'is-decreased'}`}>
            {groupFactor > 1 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}{groupFactor}{copy.TIMES}
          </span>
        )}
      </div>
      <div className="mp-product-card-body">
        <div className="mp-product-meta"><span>{product.SKU}</span><strong>{product.PRICE}</strong></div>
        <h3>{product.PRODUCT_NAME}</h3>
        <p>{product.MATERIAL} · {product.CATEGORY}</p>
      </div>
    </article>
  );
};

ProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  change: PropTypes.object,
  displayPosition: PropTypes.number.isRequired,
  dragging: PropTypes.bool.isRequired,
  dropTarget: PropTypes.bool.isRequired,
  groupExcluded: PropTypes.bool.isRequired,
  groupFactor: PropTypes.number.isRequired,
  copy: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
  onPin: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDropAt: PropTypes.func.isRequired,
  onMoveEarlier: PropTypes.func.isRequired,
  onMoveLater: PropTypes.func.isRequired,
  onUnpin: PropTypes.func.isRequired,
  interactive: PropTypes.bool,
};

ProductCard.defaultProps = { change: null, interactive: true };

const groupPriorityFactors = [0.25, 0.5, 1, 2, 4];

export const FacetControl = ({ item, effect, expanded, onChange, onToggle, copy }) => {
  const [priorityStep, setPriorityStep] = useState({ direction: null, token: 0 });
  const type = effect?.TYPE || 'natural';
  const excluded = type === 'exclude';
  const factor = excluded ? 1 : (effect?.FACTOR || 1);
  const factorIndex = Math.max(0, groupPriorityFactors.indexOf(factor));
  const status = factor > 1
    ? `${copy.INCREASED} · ${factor}×`
    : (factor < 1 ? `${copy.DECREASED} · ${factor}×` : copy.NATURAL_ORDER);
  const updatePriority = (requestedIndex, direction) => {
    const nextIndex = Math.max(0, Math.min(groupPriorityFactors.length - 1, requestedIndex));
    if (nextIndex === factorIndex) return;
    const nextFactor = groupPriorityFactors[nextIndex];
    setPriorityStep((current) => ({
      direction: direction || (nextIndex > factorIndex ? 'up' : 'down'),
      token: current.token + 1,
    }));
    if (nextFactor === 1) onChange(null);
    else onChange({ TYPE: nextFactor > 1 ? 'boost' : 'bury', FACTOR: nextFactor });
  };
  const toggleExcluded = () => {
    onChange(excluded ? null : { TYPE: 'exclude' });
    if (!excluded && expanded) onToggle();
  };
  return (
    <article className={`mp-facet-rule is-${type} ${expanded ? 'is-expanded' : ''}`} style={{ '--mp-effect-strength': Math.abs(factorIndex - 2) }}>
      <div className="mp-facet-rule-main">
        <button type="button" className="mp-facet-summary" aria-expanded={expanded} onClick={onToggle}>
          <span className="mp-facet-identity"><strong title={item.NAME}>{item.NAME}</strong><small>{item.COUNT} {copy.PRODUCTS_LOWER}</small></span>
          <span className="mp-facet-current" aria-live="polite">{excluded ? copy.EXCLUDED : status}</span>
        </button>
        <div className="mp-facet-row-actions">
          <Tooltip title={expanded ? copy.DONE : copy.EDIT_PRIORITY}>
            <IconButton className="mp-facet-edit" aria-label={`${expanded ? copy.DONE_EDITING : copy.EDIT_PRIORITY} ${item.NAME}`} aria-expanded={expanded} onClick={onToggle}>
              {expanded ? <CheckIcon /> : <EditOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={excluded ? copy.RESTORE : copy.EXCLUDE}>
            <IconButton className="mp-facet-exclude" aria-label={`${excluded ? copy.RESTORE : copy.EXCLUDE} ${item.NAME}`} onClick={toggleExcluded}>
              {excluded ? <UndoIcon /> : <CloseIcon />}
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {expanded && (
        <div className="mp-facet-inline-editor">
          <div className="mp-facet-editor-heading">
            <span>{copy.PRIORITY}</span>
            <strong
              key={`${type}-${factor}-${priorityStep.token}`}
              className={priorityStep.direction ? `mp-facet-priority-feedback is-step-${priorityStep.direction}` : ''}
            >
              {status}
            </strong>
          </div>
          <div className="mp-facet-scale">
            <button
              type="button"
              className="mp-facet-step mp-facet-step--down"
              aria-label={`${copy.LOWER} ${copy.PRIORITY}: ${item.NAME}`}
              title={`${copy.LOWER} ${copy.PRIORITY}`}
              disabled={factorIndex === 0}
              onClick={() => updatePriority(factorIndex - 1, 'down')}
            >
              <ArrowDownwardIcon aria-hidden="true" />
            </button>
            <input type="range" min="0" max="4" step="1" value={factorIndex} aria-label={`${copy.PRIORITY_STRENGTH} ${item.NAME}`} aria-valuetext={status} onChange={(event) => updatePriority(Number(event.target.value))} />
            <button
              type="button"
              className="mp-facet-step mp-facet-step--up"
              aria-label={`${copy.HIGHER} ${copy.PRIORITY}: ${item.NAME}`}
              title={`${copy.HIGHER} ${copy.PRIORITY}`}
              disabled={factorIndex === groupPriorityFactors.length - 1}
              onClick={() => updatePriority(factorIndex + 1, 'up')}
            >
              <ArrowUpwardIcon aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

FacetControl.propTypes = {
  item: PropTypes.object.isRequired,
  effect: PropTypes.object,
  expanded: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  copy: PropTypes.object.isRequired,
};

FacetControl.defaultProps = { effect: null };
